import type {ReactElement} from 'react';
import React, {useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {TextListThumbnailOptions} from '@Panels/poster-editor-item-list-panel/components/text-list-thumbnail-options';
import {TextListMobileColorPanel} from '@Panels/poster-editor-item-list-panel/components/text-list-mobile-color-panel';
import {TextSlideListColorPanel} from '@Panels/poster-editor-item-list-panel/components/text-slide-list-color-panel';
import {TextSlideThumbnailOption} from '@Panels/poster-editor-item-list-panel/components/text-slide-thumbnail-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

interface PosterEditorItemListPanelProps {
  panelId: string;
}

enum ListOptionPanels {
  DEFAULT = 'DEFAULT',
  COLOR_PICKER = 'COLOR_PICKER',
}

export default function PosterEditorItemListPanel({...props}: PosterEditorItemListPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;

  const [panelToShow, setPanelToShow] = useState(ListOptionPanels.DEFAULT);

  if (!activeItem) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_list')} />;
  };

  const getContent = (): ReactElement | undefined => {
    if (activeItem.isText()) {
      return (
        <TextListThumbnailOptions
          onClickColorFill={(): void => {
            setPanelToShow(ListOptionPanels.COLOR_PICKER);
          }}
          panelId={props.panelId}
        />
      );
    }

    if (activeItem.isSlideshow() && activeSlide?.isTextSlide()) {
      return (
        <TextSlideThumbnailOption
          panelId={props.panelId}
          onClickColorFill={(): void => {
            setPanelToShow(ListOptionPanels.COLOR_PICKER);
          }}
        />
      );
    }

    return undefined;
  };

  const getPanels = (): ReactElement | null => {
    switch (panelToShow) {
      case ListOptionPanels.COLOR_PICKER:
        return getColorPickers();
      case ListOptionPanels.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  const getColorPickers = (): ReactElement | null => {
    if (activeItem.isText()) {
      return (
        <TextListMobileColorPanel
          panelId={props.panelId}
          onBack={(): void => {
            setPanelToShow(ListOptionPanels.DEFAULT);
          }}
          onClose={onClose}
        />
      );
    }

    if (activeItem.isSlideshow() && activeSlide?.isTextSlide()) {
      return (
        <TextSlideListColorPanel
          panelId={props.panelId}
          onBack={(): void => {
            setPanelToShow(ListOptionPanels.DEFAULT);
          }}
          onClose={onClose}
        />
      );
    }
    return null;
  };

  return getPanels();
}
